import React, { useState, useEffect, ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { FixedSizeList as List } from 'react-window';
import { actions as newProjectWizardActions } from '../../../actions/newProjectWizardActions';
import * as types from '../../../constants/actionTypes';
import { Project, LocationOption, ProjectLocationSearchParams } from '../../../types/common';
import Notification from '../../Common/Notification';
import './ProjectLocations.scss';
import FilterLocationsDrawer from './FilterLocationsDrawer';

interface ProjectLocationsProps {
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	projectData: Project;
	setProjectData: React.Dispatch<React.SetStateAction<Project>>;
	subscriptionId: number;
	locationsState: LocationsState;
}

interface LocationsState {
	availableLocations: Array<LocationOption>;
	selectedLocations: Array<LocationOption>;
	setAvailableLocations: React.Dispatch<React.SetStateAction<Array<LocationOption>>>;
	setSelectedLocations: React.Dispatch<React.SetStateAction<Array<LocationOption>>>;
}

const ProjectLocations = ({ setIsLoading, projectData, setProjectData, subscriptionId, locationsState }: ProjectLocationsProps): ReactElement => {
	const dispatch: Dispatch = useDispatch();
	const getLocations = async (params) => dispatch(newProjectWizardActions.searchLocations(params));

	const [searchParamsState, setSearchParamsState] = useState<ProjectLocationSearchParams>({
		subscriptionId: subscriptionId,
		projectKey: projectData.projectKey,
		areaIds: [],
		brandIds: [],
		continentIds: [],
		countryIds: [],
		managementCompanyIds: [],
		typeIds: [],
		regionIds: [],
		tagIds: [],
		abbreviations: [],
	});

	const [availableFilter, setAvailableFilter] = useState<string>('');
	const [selectedFilter, setSelectedFilter] = useState<string>('');
	const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);

	useEffect(() => {
		loadLocations(searchParamsState);
	}, []);

	const loadLocations = async (searchParams: ProjectLocationSearchParams) => {
		setIsLoading(true);

		try {
			const response = await getLocations(searchParams);
			if (response.type === types.NEW_PROJECT_WIZARD_LOCATION_SEARCH_SUCCESS) {
				locationsState.setAvailableLocations(response.payload.locations);
			} else if (response.type === types.NEW_PROJECT_WIZARD_LOCATION_SEARCH_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const renderAvailableLocations = () => {
		const filterLower = availableFilter.toLocaleLowerCase();

		const items = locationsState.availableLocations
			.filter((x) => !locationsState.selectedLocations.some((sl) => sl.id === x.id)) // Suppress any locations that have been selected
			.filter((x) => x.name.toLocaleLowerCase().indexOf(filterLower) >= 0)
			.sort((a, b) => (a.name > b.name ? 1 : -1));

		return (
			<List
				className='list-group'
				height={350}
				itemCount={items.length}
				itemSize={50}
				width={'100%'}
				itemData={items}
				itemKey={(index, data) => {
					// Find the item at the specified index. "data" is the array of locations that was passed in "itemData"
					const item = data[index];
					return item.id;
				}}>
				{({ data, index, style }) => {
					const item = data[index];

					return (
						<div style={style} className='list-group-item' id={item.id.toString()} onClick={() => selectLocation(item.id)}>
							<div className='d-flex w-100 justify-content-between'>
								<span className='list-item-text align-middle'>{item.name}</span>
								<div className='list-item-icon'>
									<i className='fa-regular fa-plus'></i>
								</div>
							</div>
						</div>
					);
				}}
			</List>
		);
	};

	const renderSelectedLocations = () => {
		const filterLower = selectedFilter.toLocaleLowerCase();

		const items = locationsState.selectedLocations
			.filter((x) => x.name.toLocaleLowerCase().indexOf(filterLower) >= 0)
			.sort((a, b) => (a.name > b.name ? 1 : -1));

		return (
			<List
				className='list-group'
				height={350}
				itemCount={items.length}
				itemSize={50}
				width={'100%'}
				itemData={items}
				itemKey={(index, data) => {
					// Find the item at the specified index. "data" is the array of locations that was passed in "itemData"
					const item = data[index];
					return item.id;
				}}>
				{({ data, index, style }) => {
					const item = data[index];

					return (
						<div
							style={style}
							className='list-group-item'
							key={'selected-loc-' + index}
							id={item.id.toString()}
							onClick={() => deselectLocation(item.id)}>
							<div className='d-flex w-100 justify-content-between'>
								<span className='list-item-text align-middle'>{item.name}</span>
								<div className='list-item-icon'>
									<i className='fa-regular fa-minus'></i>
								</div>
							</div>
						</div>
					);
				}}
			</List>
		);
	};

	const selectLocation = (locId: number) => {
		const selected = locationsState.availableLocations.find((x) => x.id === locId);

		locationsState.setAvailableLocations((existing) => existing.filter((x) => x.id !== locId));
		locationsState.setSelectedLocations((existing) => [...existing, selected]);

		setProjectData((existing) => ({
			...existing,
			locationIds: [...existing.locationIds, selected.id],
		}));
	};

	const deselectLocation = (locId: number) => {
		const selected = locationsState.selectedLocations.find((x) => x.id === locId);

		locationsState.setSelectedLocations((existing) => existing.filter((x) => x.id !== locId));
		setProjectData((existing) => ({
			...existing,
			locationIds: existing.locationIds.filter((x) => x !== locId),
		}));

		locationsState.setAvailableLocations((existing) => [...existing, selected]);
	};

	const selectAll = () => {
		locationsState.setSelectedLocations((existing) => [...existing, ...locationsState.availableLocations]);
		locationsState.setAvailableLocations([]);

		const availableIds = locationsState.availableLocations.map((loc) => loc.id);

		setProjectData((existing) => ({
			...existing,
			locationIds: [...existing.locationIds, ...availableIds],
		}));
	};

	const deselectAll = () => {
		locationsState.setAvailableLocations((existing) => [...existing, ...locationsState.selectedLocations]);
		locationsState.setSelectedLocations([]);

		setProjectData((existing) => ({
			...existing,
			locationIds: [],
		}));
	};

	const onFilter = async (searchParams: ProjectLocationSearchParams) => {
		//filters.projectKey = projectData.projectKey;
		await loadLocations(searchParams);
		setSearchParamsState(searchParams);
		setIsFilterDrawerOpen(false);
	};

	return (
		<div className='project-locations-content'>
			<div className='wizard-content-title'>Which teams or locations will complete this project?</div>

			<div className='container'>
				<div className='row'>
					{/* Begin available */}
					<div className='col-md-6'>
						<div className='row locations-section-top'>
							<div className='col-9 my-3'>
								<span className='wizard-content-title'>Available</span>
							</div>
							<div className='col-3'>{/* Future spot of "create new location" */}</div>
						</div>

						<div className='row locations-section'>
							<div className='col-10 py-2'>
								<div className='input-group'>
									<input
										type='text'
										className='form-control'
										id='search-available'
										placeholder='Search by name'
										aria-label='Search by name'
										aria-describedby='search-addon'
										value={availableFilter}
										onChange={(e) => setAvailableFilter(e.target.value)}
									/>
									<span className='input-group-text bg-transparent' id='search-addon'>
										<i className='far fa-magnifying-glass'></i>
									</span>
								</div>
							</div>
							<div className='col-2'>
								<div className='filters-buttons' onClick={() => setIsFilterDrawerOpen(true)}>
									<span className='text-nowrap'>
										<i className='fa-regular fa-bars-filter'></i> Filters
									</span>
								</div>
							</div>
						</div>

						<div className='row locations-section'>
							<div className='col-12 list-container'>{renderAvailableLocations()}</div>
						</div>

						<div className='row locations-section-bottom'>
							<div className='col-12'>
								<button type='button' className='btn pacer61-purple-btn' onClick={() => selectAll()}>
									Select All
								</button>
							</div>
						</div>
					</div>

					{/* Begin selected */}
					<div className='col-md-6'>
						<div className='row locations-section-top'>
							<div className='col-9 my-3'>
								<span className='wizard-content-title'>Selected</span>
							</div>
						</div>

						<div className='row locations-section'>
							<div className='col-12 py-2'>
								<div className='input-group'>
									<input
										type='text'
										className='form-control'
										id='search-selected'
										placeholder='Search by name'
										aria-label='Search by name'
										aria-describedby='search-addon'
										value={selectedFilter}
										onChange={(e) => setSelectedFilter(e.target.value)}
									/>
									<span className='input-group-text bg-transparent' id='search-addon'>
										<i className='far fa-magnifying-glass'></i>
									</span>
								</div>
							</div>
						</div>

						<div className='row locations-section'>
							<div className='col-12 list-container'>{renderSelectedLocations()}</div>
						</div>

						<div className='row locations-section-bottom'>
							<div className='col-12'>
								<button type='button' className='btn pacer61-purple-btn' onClick={() => deselectAll()}>
									Deselect All
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			{isFilterDrawerOpen && (
				<FilterLocationsDrawer
					subscriptionId={subscriptionId}
					onCancel={() => setIsFilterDrawerOpen(false)}
					onFilter={onFilter}
					searchParams={searchParamsState}
					setSearchParams={setSearchParamsState}
				/>
			)}
		</div>
	);
};

export default ProjectLocations;
